import { useEffect, useState } from "react";
import axios from "axios";
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Divider, IconButton, MenuItem, Stack,
  TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DrawIcon from "@mui/icons-material/Draw";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { API_BASE_URL } from "../config";
import { ATTENDANCE_ROLES, ATTENDANCE_STATUSES, STATUS_COLOR } from "../constants";
import { exportEventPersonnel } from "../exportExcel";
import { useMe } from "../MeContext";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";
import AttendanceDialog from "./AttendanceDialog";
import BulkContractDialog from "./BulkContractDialog";
import PersonnelDialog from "./PersonnelDialog";

const ATT_API = `${API_BASE_URL}/attendances`;
const PERSONNEL_API = `${API_BASE_URL}/personnel`;
const TEAMS_API = `${API_BASE_URL}/event-teams`;

// Position of `value` in a fixed option list, with unset/unrecognised values last
// — so role and status sorts follow the order the dropdowns offer.
const rankIn = (list, value) => {
  const i = value ? list.indexOf(value) : -1;
  return i === -1 ? list.length : i;
};

// Surname-first key, matching how the Personnel roster is ordered.
const attendeeName = (a) =>
  (a.person ? `${a.person.surname} ${a.person.forename}` : "").trim().toLowerCase();

// Picker label — name plus email, to tell apart people who share a name.
const personLabel = (p) =>
  `${p.forename || ""} ${p.surname || ""}`.trim() + (p.email ? ` (${p.email})` : "");

/**
 * Who is on one event, and the teams they're grouped into. Everything here saves
 * instantly (there is no Save button): the attendance rows, their roles/statuses,
 * and the team names. Team *membership* is set per person, in the attendance
 * dialog this opens.
 *
 * Props:
 *  - event:   the event record ({id, label_long, …}); null when closed
 *  - open:    boolean
 *  - onClose: () => void
 */
export default function EventPersonnelDialog({ event, open, onClose }) {
  const { can } = useMe();
  const fullScreen = useFullScreenDialog();
  const eventId = event?.id;

  const [people, setPeople] = useState([]);
  const [roster, setRoster] = useState([]);      // the whole roster, for the "add personnel" picker
  const [personById, setPersonById] = useState({}); // full personnel records, keyed by id (for export)
  const [sortBy, setSortBy] = useState("role");  // role | name | status
  const [editAtt, setEditAtt] = useState(null);  // attendance shown in the edit dialog
  const [attDraft, setAttDraft] = useState(null); // person chosen in the picker, not yet added
  const [teams, setTeams] = useState([]);
  const [teamDraft, setTeamDraft] = useState("");   // name typed in the "add team" field
  const [renameTeam, setRenameTeam] = useState(null); // {id, name} being renamed
  const [renameDraft, setRenameDraft] = useState("");
  const [deleteTeam, setDeleteTeam] = useState(null); // team awaiting delete confirmation
  const [editPerson, setEditPerson] = useState(null); // personnel id whose whole record is open
  const [bulkOpen, setBulkOpen] = useState(false);    // send engagement schedules
  const { busy: adding, run: runWrite } = useWriteLock();

  const refreshPeople = () =>
    axios.get(ATT_API + "/", { params: { event_id: eventId } }).then(res => setPeople(res.data));
  const refreshTeams = () =>
    axios.get(TEAMS_API + "/", { params: { event_id: eventId } }).then(res => setTeams(res.data));

  // Load on open. Deliberately not cleared on close, so the content doesn't blank
  // out mid fade-out; the next open refetches anyway.
  useEffect(() => {
    if (!open || !eventId) return;
    setEditAtt(null);
    setAttDraft(null);
    setTeamDraft("");
    setRenameTeam(null);
    setDeleteTeam(null);
    refreshPeople();
    refreshTeams();
    // The roster is only needed here (picker + the full records the export writes),
    // so it is fetched with the dialog rather than by the page behind it.
    axios.get(PERSONNEL_API + "/").then(res => {
      const map = {};
      res.data.forEach(p => { map[p.id] = p; });
      setPersonById(map);
      setRoster(res.data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId]);

  const canEdit = can("EventAttendances.edit");
  const canDelete = can("EventAttendances.delete");
  const canOpenRecord = can("Personnel.viewAll");

  // Only people not already on the event can be added.
  const availableRoster = roster.filter(p => !people.some(a => a.personnel_id === p.id));

  // Add someone to this event, then open the dialog so their role, status, fee and
  // shift details can be filled in (the row itself carries no detail yet).
  // Both Add buttons post, and the picker is only cleared once the row comes back,
  // so a double-tap put the same person on the event twice (see useWriteLock).
  const addPerson = () => runWrite(() => {
    if (!attDraft) return null;
    return axios.post(ATT_API + "/", { personnel_id: attDraft.id, event_id: eventId })
      .then(res => { setAttDraft(null); refreshPeople(); setEditAtt(res.data); });
  });

  const addTeam = () => runWrite(() => {
    const name = teamDraft.trim();
    if (!name) return null;
    return axios.post(TEAMS_API + "/", { event_id: eventId, name })
      .then(() => { setTeamDraft(""); refreshTeams(); });
  });

  const saveTeamName = () => {
    const name = renameDraft.trim();
    if (!name) return;
    axios.put(`${TEAMS_API}/${renameTeam.id}`, { name })
      .then(() => { setRenameTeam(null); refreshTeams(); refreshPeople(); });
  };

  const removeTeam = () =>
    axios.delete(`${TEAMS_API}/${deleteTeam.id}`)
      .then(() => { setDeleteTeam(null); refreshTeams(); refreshPeople(); });

  const teamMemberNames = (t) =>
    (t.members || []).map(m => `${m.forename} ${m.surname}`).join(", ");

  // Role and status sort in dropdown order (unset last), each falling back to
  // name; the export follows whatever is on screen.
  const sortedPeople = [...people].sort((a, b) => {
    const byName = attendeeName(a).localeCompare(attendeeName(b));
    if (sortBy === "name") return byName;
    const list = sortBy === "status" ? ATTENDANCE_STATUSES : ATTENDANCE_ROLES;
    const value = (x) => (sortBy === "status" ? x.status : x.role);
    return (rankIn(list, value(a)) - rankIn(list, value(b))) || byName;
  });

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>Personnel — {event?.label_long || event?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                       gap: 1, flexWrap: "wrap" }}>
              {people.length > 1 ? (
                <TextField label="Sort by" select size="small" sx={{ minWidth: 160 }}
                           value={sortBy} onChange={e => setSortBy(e.target.value)}>
                  <MenuItem value="role">Event role</MenuItem>
                  <MenuItem value="name">Name (A–Z)</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </TextField>
              ) : <Box />}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {can("Contracts.personnel") && (
                  <Button size="small" variant="outlined" startIcon={<DrawIcon />}
                          disabled={people.length === 0} onClick={() => setBulkOpen(true)}>
                    Send contracts
                  </Button>
                )}
                <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                        disabled={people.length === 0}
                        onClick={() => exportEventPersonnel(event, sortedPeople, personById,
                                                           { includeFee: canEdit })}>
                  Export to Excel
                </Button>
              </Box>
            </Box>
            {/* Role, status, fee and shift details are edited in a dialog
                (shared with the Personnel page); removal lives in there too. */}
            {sortedPeople.map(a => (
              <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {a.person ? `${a.person.forename} ${a.person.surname}` : `#${a.personnel_id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[a.role || "No event role set", a.team?.name].filter(Boolean).join(" · ")}
                  </Typography>
                </Box>
                {a.status && <Chip size="small" label={a.status} color={STATUS_COLOR[a.status] || "default"} />}
                {(canEdit || canDelete) && (
                  <Tooltip title={canEdit ? "Edit event details" : "View event details"}>
                    <IconButton size="small" onClick={() => setEditAtt(a)}
                                aria-label={`Edit ${a.person ? `${a.person.forename} ${a.person.surname}` : "attendance"}`}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {/* The person's whole roster record — the same dialog the Personnel
                    page uses, over this list. */}
                {canOpenRecord && (
                  <Tooltip title="Open personnel record">
                    <IconButton size="small" onClick={() => setEditPerson(a.personnel_id)}
                                aria-label={`Personnel record for ${a.person ? `${a.person.forename} ${a.person.surname}` : `#${a.personnel_id}`}`}>
                      <ContactPageIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
            {people.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No personnel associated yet — add someone below.
              </Typography>
            )}

            {/* Add someone by typing their name; role/status/fee/shift details
                are then set in the dialog that opens. */}
            {can("EventAttendances.add") && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <Autocomplete
                  size="small"
                  sx={{ flex: 1 }}
                  options={availableRoster}
                  value={attDraft}
                  onChange={(_, v) => setAttDraft(v)}
                  getOptionLabel={personLabel}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  noOptionsText={roster.length ? "Everyone is already on this event" : "No personnel"}
                  renderInput={(p) => <TextField {...p} label="Add personnel" />} />
                <Button size="small" variant="outlined"
                        startIcon={adding ? <CircularProgress size={16} /> : <AddIcon />}
                        sx={{ mt: 0.5, flexShrink: 0 }}
                        disabled={!attDraft || adding} onClick={addPerson}>
                  Add
                </Button>
              </Box>
            )}

            {/* Teams (named groups of the people above), saved instantly. Who is
                in each one is set per person, in their attendance dialog. */}
            <Divider textAlign="left">Teams ({teams.length})</Divider>
            {teams.map(t => (
              <Box key={t.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>{t.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {teamMemberNames(t) || "No one in this team yet"}
                  </Typography>
                </Box>
                {canEdit && (
                  <>
                    <Tooltip title="Rename team">
                      <IconButton size="small" aria-label={`Rename ${t.name}`}
                                  onClick={() => { setRenameTeam(t); setRenameDraft(t.name); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete team">
                      <IconButton size="small" color="error" aria-label={`Delete ${t.name}`}
                                  onClick={() => setDeleteTeam(t)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            ))}
            {teams.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No teams yet. Add one, then put people in it from their entry in the
                Personnel list above — an incident allocated to a team is visible to
                everyone in it.
              </Typography>
            )}
            {canEdit && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField size="small" label="Add team" sx={{ flex: 1 }}
                           value={teamDraft} onChange={e => setTeamDraft(e.target.value)}
                           onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTeam(); } }}
                           placeholder="e.g. Response Team 1" />
                <Button size="small" variant="outlined"
                        startIcon={adding ? <CircularProgress size={16} /> : <AddIcon />}
                        sx={{ mt: 0.5, flexShrink: 0 }}
                        disabled={!teamDraft.trim() || adding} onClick={addTeam}>
                  Add
                </Button>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Attendance (role / status / fee / shift details) — shared with Personnel */}
      <AttendanceDialog
        attendance={editAtt}
        subject={editAtt?.person
          ? `${editAtt.person.forename} ${editAtt.person.surname}`
          : `Attendance #${editAtt?.id}`}
        onClose={() => setEditAtt(null)}
        // Teams too: the dialog is where membership is set, so the Teams section's
        // member lists move whenever an attendance is saved or removed.
        onSaved={() => { refreshPeople(); refreshTeams(); }}
        canEdit={canEdit}
        canDelete={canDelete} />

      {/* Engagement schedules for everyone not yet sent one */}
      <BulkContractDialog open={bulkOpen} event={event} attendances={sortedPeople}
                          onClose={() => setBulkOpen(false)} onDone={refreshPeople} />

      {/* One person's whole record, over this list — shared with the Personnel page.
          Its own Events section can change this event's attendance (including team
          membership), so both lists are reloaded when it closes. */}
      <PersonnelDialog
        personId={editPerson}
        onClose={() => { setEditPerson(null); refreshPeople(); refreshTeams(); }} />

      {/* Team rename */}
      <Dialog open={renameTeam !== null} onClose={() => setRenameTeam(null)} fullWidth maxWidth="xs">
        <DialogTitle>Rename team</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Team name" sx={{ mt: 1 }}
                     value={renameDraft} onChange={e => setRenameDraft(e.target.value)}
                     onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); saveTeamName(); } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTeam(null)}>Cancel</Button>
          <Button variant="contained" disabled={!renameDraft.trim()} onClick={saveTeamName}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Team delete confirmation */}
      <Dialog open={deleteTeam !== null} onClose={() => setDeleteTeam(null)}>
        <DialogTitle>Delete team?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deleteTeam?.name}" will be removed from this event.
            {deleteTeam?.members?.length
              ? ` Its ${deleteTeam.members.length} member${deleteTeam.members.length === 1 ? "" : "s"}
                  stay on the event but will no longer be in a team.`
              : ""}
            {" "}Any incident allocated to it keeps the name on record, but stops being
            anyone's. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTeam(null)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />}
                  onClick={removeTeam}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
